import {
  ComponentType,
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  Injector,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Observable, map, takeUntil } from 'rxjs';

import { ArrowPosition, PopoverData } from './popover-data.interface';
import { PopoverRef } from './popover-ref';
import { PopoverComponent } from './popover.component';
import { PopoverPosition } from './popover.model';
import { POPOVER_DATA } from './popover.tokens';

@Directive({
  selector: '[appPopover]',
  exportAs: 'appPopover',
  host: {
    ['[class.cursor-pointer]']: 'trigger() === "click"',
    ['[attr.aria-haspopup]']: 'true',
    ['[attr.aria-expanded]']: 'isShown()',
  },
  standalone: true,
})
export class PopoverDirective<Data = undefined> implements OnChanges, OnDestroy {
  readonly #overlay = inject(Overlay);
  readonly #overlayPositionBuilder = inject(OverlayPositionBuilder);
  readonly #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #injector = inject(Injector);
  readonly #vcr = inject(ViewContainerRef);
  readonly #destroyRef = inject(DestroyRef);

  public popover = input<string | TemplateRef<unknown> | ComponentType<unknown>>('', {
    alias: 'appPopover',
  });
  public popoverData = input<Data>();
  public position = input<PopoverPosition>('bottom-middle');
  public trigger = input<'click' | 'hover' | 'manual'>('hover');
  public isPopoverOpen = input<boolean>(false);
  public popoverClass = input<string>('');
  public popoverContainerClass = input<string>('');
  public popoverPanelClass = input<string>('');
  public showArrow = input<boolean>(true);
  public scrollStrategy = input<'close' | 'reposition' | 'noop'>('close');

  public popoverClose = output();

  readonly #overlayOffset = 2;
  readonly #scrollStrategy = computed(() => {
    switch (this.scrollStrategy()) {
      case 'reposition': {
        return this.#overlay.scrollStrategies.reposition({
          autoClose: true,
        });
      }

      case 'noop': {
        return this.#overlay.scrollStrategies.noop();
      }

      case 'close': {
        return this.#overlay.scrollStrategies.close();
      }
    }
  });
  #positionStrategy = this.#createPositionStrategy();
  #overlayRef?: OverlayRef;
  #popoverRef?: PopoverRef;
  public readonly isShown = signal(false);
  readonly #defaultArrowPadding = 24;

  readonly #positionConnectedPositionMap: Record<PopoverPosition, ConnectedPosition> = {
    'bottom-left': this.#getBottomLeftPosition(),
    'bottom-middle': this.#getBottomMiddlePosition(),
    'bottom-right': this.#getBottomRightPosition(),
    'top-left': this.#getTopLeftPosition(),
    'top-middle': this.#getTopMiddlePosition(),
    'top-right': this.#getTopRightPosition(),
    'left-middle': this.#getLeftMiddlePosition(),
    'right-middle': this.#getRightMiddlePosition(),
    'right-bottom': this.#getRightBottomPosition(),
  };

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if ((event.key !== 'Enter' && event.key !== ' ') || this.isShown()) {
      return;
    }
    this.#show();
    event.preventDefault();
  }

  @HostListener('window:keydown', ['$event'])
  public onWindowKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }
    this.close();
    event.preventDefault();
  }

  @HostListener('click')
  public onClick(): void {
    if (this.trigger() !== 'click') {
      return;
    }

    this.isShown.set(true);
    this.#show();
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    if (this.trigger() !== 'hover') {
      return;
    }

    this.isShown.set(true);
    this.#show();
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    if (this.trigger() !== 'hover' || !this.#popoverRef) {
      return;
    }

    this.close();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['position']) {
      this.#updatePositionStrategy();
    }
    if (changes['popover'] && this.isShown()) {
      this.#show();
    }
    if (changes['isPopoverOpen'] && this.trigger() === 'manual') {
      if (this.isPopoverOpen()) {
        this.isShown.set(true);
        this.#show();
      } else {
        this.close();
      }
    }
  }

  public ngOnDestroy(): void {
    if (!this.#popoverRef) return;
    this.close();
  }

  public close(): void {
    if (!this.#popoverRef) return;

    this.isShown.set(false);
    this.#popoverRef.close();
    this.popoverClose.emit();
  }

  public updatePosition(): void {
    this.#overlayRef?.updatePosition();
  }

  #show(): void {
    if (!this.popover()) {
      return;
    }

    if (this.#overlayRef) {
      this.#overlayRef.detach();
    } else {
      this.#overlayRef = this.#createOverlayRef();
    }

    this.#popoverRef = new PopoverRef(this.#overlayRef);
    this.#popoverRef
      .afterClosed$()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => (this.#popoverRef = undefined));

    this.#overlayRef.attach(new ComponentPortal(PopoverComponent, this.#vcr, this.#createInjector(this.#popoverRef)));
    this.#overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.#destroyRef), takeUntil(this.#popoverRef.afterClosed$()))
      .subscribe(() => this.close());

    this.#overlayRef
      .outsidePointerEvents()
      .pipe(takeUntilDestroyed(this.#destroyRef), takeUntil(this.#popoverRef.afterClosed$()))
      .subscribe((event) => {
        if (event.target !== this.#elementRef.nativeElement && !this.#elementRef.nativeElement.contains(event.target as HTMLElement)) {
          this.close();
        }
      });

    this.#overlayRef
      .detachments()
      .pipe(takeUntilDestroyed(this.#destroyRef), takeUntil(this.#popoverRef.afterClosed$()))
      .subscribe(() => {
        this.close();
      });
  }

  #updatePositionStrategy(): void {
    this.#positionStrategy = this.#createPositionStrategy();
    if (this.#overlayRef) {
      this.#overlayRef.updatePositionStrategy(this.#positionStrategy);
    }
  }

  #createPositionStrategy(): FlexibleConnectedPositionStrategy {
    return this.#overlayPositionBuilder.flexibleConnectedTo(this.#elementRef).withPositions(this.#getPositions());
  }

  #getPositions(): ConnectedPosition[] {
    switch (this.position()) {
      case 'bottom-left': {
        return this.#getBottomLeftPositions();
      }
      case 'bottom-middle': {
        return this.#getBottomMiddlePositions();
      }
      case 'bottom-right': {
        return this.#getBottomRightPositions();
      }
      case 'top-left': {
        return this.#getTopLeftPositions();
      }
      case 'top-middle': {
        return this.#getTopMiddlePositions();
      }
      case 'top-right': {
        return this.#getTopRightPositions();
      }
      case 'left-middle': {
        return this.#getLeftMiddlePositions();
      }
      case 'right-middle': {
        return this.#getRightMiddlePositions();
      }
      case 'right-bottom': {
        return this.#getRightBottomPositions();
      }
    }
  }

  #getBottomLeftPositions(): ConnectedPosition[] {
    return [
      this.#getBottomLeftPosition(),
      this.#getBottomMiddlePosition(),
      this.#getBottomRightPosition(),
      this.#getTopLeftPosition(),
      this.#getTopMiddlePosition(),
      this.#getTopRightPosition(),
      this.#getLeftMiddlePosition(),
      this.#getRightMiddlePosition(),
    ];
  }

  #getBottomMiddlePositions(): ConnectedPosition[] {
    return [
      this.#getBottomMiddlePosition(),
      this.#getBottomLeftPosition(),
      this.#getBottomRightPosition(),
      this.#getTopMiddlePosition(),
      this.#getTopLeftPosition(),
      this.#getTopRightPosition(),
      this.#getLeftMiddlePosition(),
      this.#getRightMiddlePosition(),
    ];
  }

  #getBottomRightPositions(): ConnectedPosition[] {
    return [
      this.#getBottomRightPosition(),
      this.#getBottomMiddlePosition(),
      this.#getBottomLeftPosition(),
      this.#getTopRightPosition(),
      this.#getTopMiddlePosition(),
      this.#getTopLeftPosition(),
      this.#getLeftMiddlePosition(),
      this.#getRightMiddlePosition(),
    ];
  }

  #getTopLeftPositions(): ConnectedPosition[] {
    return [
      this.#getTopLeftPosition(),
      this.#getTopMiddlePosition(),
      this.#getTopRightPosition(),
      this.#getBottomLeftPosition(),
      this.#getBottomMiddlePosition(),
      this.#getBottomRightPosition(),
      this.#getLeftMiddlePosition(),
      this.#getRightMiddlePosition(),
    ];
  }

  #getTopMiddlePositions(): ConnectedPosition[] {
    return [
      this.#getTopMiddlePosition(),
      this.#getTopLeftPosition(),
      this.#getTopRightPosition(),
      this.#getBottomMiddlePosition(),
      this.#getBottomLeftPosition(),
      this.#getBottomRightPosition(),
      this.#getLeftMiddlePosition(),
      this.#getRightMiddlePosition(),
    ];
  }

  #getTopRightPositions(): ConnectedPosition[] {
    return [
      this.#getTopRightPosition(),
      this.#getTopMiddlePosition(),
      this.#getTopLeftPosition(),
      this.#getBottomRightPosition(),
      this.#getBottomMiddlePosition(),
      this.#getBottomLeftPosition(),
      this.#getLeftMiddlePosition(),
      this.#getRightMiddlePosition(),
    ];
  }

  #getLeftMiddlePositions(): ConnectedPosition[] {
    return [
      this.#getLeftMiddlePosition(),
      this.#getBottomLeftPosition(),
      this.#getTopLeftPosition(),
      this.#getBottomMiddlePosition(),
      this.#getTopMiddlePosition(),
      this.#getBottomRightPosition(),
      this.#getTopRightPosition(),
      this.#getRightMiddlePosition(),
    ];
  }

  #getRightMiddlePositions(): ConnectedPosition[] {
    return [
      this.#getRightMiddlePosition(),
      this.#getBottomRightPosition(),
      this.#getTopRightPosition(),
      this.#getBottomMiddlePosition(),
      this.#getTopMiddlePosition(),
      this.#getBottomLeftPosition(),
      this.#getTopLeftPosition(),
      this.#getLeftMiddlePosition(),
    ];
  }

  #getRightBottomPositions(): ConnectedPosition[] {
    return [
      this.#getRightBottomPosition(),
      this.#getRightMiddlePosition(),
      this.#getBottomRightPosition(),
      this.#getTopRightPosition(),
      this.#getBottomMiddlePosition(),
      this.#getTopMiddlePosition(),
      this.#getBottomLeftPosition(),
      this.#getTopLeftPosition(),
      this.#getLeftMiddlePosition(),
    ];
  }

  #getBottomLeftPosition(): ConnectedPosition {
    return {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: this.#overlayOffset,
    };
  }

  #getBottomMiddlePosition(): ConnectedPosition {
    return {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: this.#overlayOffset,
    };
  }

  #getBottomRightPosition(): ConnectedPosition {
    return {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: this.#overlayOffset,
    };
  }

  #getTopLeftPosition(): ConnectedPosition {
    return {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -this.#overlayOffset,
    };
  }

  #getTopMiddlePosition(): ConnectedPosition {
    return {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -this.#overlayOffset,
    };
  }

  #getTopRightPosition(): ConnectedPosition {
    return {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -this.#overlayOffset,
    };
  }

  #getLeftMiddlePosition(): ConnectedPosition {
    return {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -this.#overlayOffset,
    };
  }

  #getRightMiddlePosition(): ConnectedPosition {
    return {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: this.#overlayOffset,
    };
  }

  #getRightBottomPosition(): ConnectedPosition {
    return {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: this.#overlayOffset,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      offsetY: this.showArrow() ? this.#defaultArrowPadding / 2 : 0,
    };
  }

  #createOverlayRef(): OverlayRef {
    return this.#overlay.create({
      positionStrategy: this.#positionStrategy,
      scrollStrategy: this.#scrollStrategy(),
      hasBackdrop: this.trigger() === 'click',
      backdropClass: '',
      panelClass: this.popoverPanelClass(),
    });
  }

  #getArrowPosition$(): Observable<ArrowPosition | undefined> {
    return this.#positionStrategy.positionChanges.pipe(
      map((positionChange): ArrowPosition | undefined => {
        if (!this.#overlayRef) {
          return;
        }

        const position = this.#getPositionFromConnectedOverlayPositionChange(positionChange);

        if (!position) {
          return;
        }

        switch (position) {
          case 'bottom-left': {
            return {
              position: 'top',
              left: `${this.#defaultArrowPadding}px`,
            };
          }
          case 'bottom-middle': {
            return {
              position: 'top',
              left: `50%`,
              translateX: '-50%',
            };
          }
          case 'bottom-right': {
            return {
              position: 'top',
              right: `${this.#defaultArrowPadding}px`,
            };
          }
          case 'top-left': {
            return {
              position: 'bottom',
              left: `${this.#defaultArrowPadding}px`,
            };
          }
          case 'top-middle': {
            return {
              position: 'bottom',
              left: `50%`,
              translateX: '-50%',
            };
          }
          case 'top-right': {
            return {
              position: 'bottom',
              right: `${this.#defaultArrowPadding}px`,
            };
          }
          case 'left-middle': {
            return {
              position: 'right',
              top: '50%',
              translateY: `-50%`,
            };
          }
          case 'right-middle': {
            return {
              position: 'left',
              top: '50%',
              translateY: `-50%`,
            };
          }
          case 'right-bottom': {
            return {
              position: 'left',
              top: `calc(100% - ${this.#defaultArrowPadding}px)`,
              translateY: `-50%`,
            };
          }
        }
      })
    );
  }

  #createInjector(popoverRef: PopoverRef): Injector {
    return Injector.create({
      providers: [
        {
          provide: POPOVER_DATA,
          useValue: {
            popover: this.popover(),
            data: this.popoverData,
            showArrow: this.showArrow,
            arrowPosition: runInInjectionContext(this.#injector, () => toSignal(this.#getArrowPosition$())),
            popoverClass: this.popoverClass,
            popoverContainerClass: this.popoverContainerClass,
          } satisfies PopoverData<Data | undefined>,
        },
        {
          provide: PopoverRef,
          useValue: popoverRef,
        },
      ],
      parent: this.#injector,
    });
  }

  #getPositionFromConnectedOverlayPositionChange(positionChange: ConnectedOverlayPositionChange): PopoverPosition | undefined {
    const connectedPositionKeys = Object.keys(this.#positionConnectedPositionMap) as PopoverPosition[];
    return connectedPositionKeys.find((key) => {
      const connectedPosition = this.#positionConnectedPositionMap[key];

      return Object.entries(connectedPosition).every(([untypedKey, value]) => {
        const key = untypedKey as keyof ConnectionPositionPair;
        const positionValue = positionChange.connectionPair[key];
        return value === positionValue;
      });
    });
  }
}
